import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { AppService } from '@App';
import { AccountSettingsDTO, RulesDTO, SectionDTO } from '@DTOs';
import { AccountSettings } from '@Enums';
import { Account, Client, UWResponse } from '@Models';
import { AccountSettingsDataService, RulesDataService, SectionDataService } from '@Services';
import { SharedModule } from '@Shared/shared.module';
import { TimeUtils, Utils } from '@Utils';
import { catchError, map, of } from 'rxjs';
import { SubSink } from 'subsink';

@Component({
    selector: 'workbench-decision-config',
    templateUrl: './workbench-decision-config.component.html',
    styleUrls: ['workbench-decision-config.component.scss'],    
    imports: [SharedModule]
})
export default class WorkbenchDecisionConfigComponent implements OnInit, OnDestroy {
    @Input() client: Client;
    @Input() account: Account;

    decisionForm: UntypedFormGroup;

    initialSectionId = '';
    initialRuleId = '';
    noSection: SectionDTO = { id: '00000000-0000-0000-0000-000000000000', name: 'None', header: '', display: true, description: '', restartScript: '', isMasked: false, referenceId: '', objectProperties: [], questions: [], sectionActions: [], sectionReflexives: [] };
    noRule: RulesDTO = { id: Utils.emptyGuid, name: 'None', accountId: '', filterId: '', jsonTestResponse: '' };
    sections: SectionDTO[] = [];
    sectionsLoaded = false;
    rules: RulesDTO[] = [];
    rulesLoaded = false;

    accountSettings: AccountSettingsDTO[] = [];
    accountSettingsLoaded = false;
    lastUpdated: Date;


    errorMessage = '';

    subs = new SubSink();

    protected get AccountSettings() { return AccountSettings; } // Make enums available in template

    constructor(
        private formBuilder: UntypedFormBuilder,
        private appService: AppService,
        private sectionDataService: SectionDataService,
        private rulesDataService: RulesDataService,
        private accountSettingsDataService: AccountSettingsDataService,
    ) {
    }

    //#region Helpers

    isValid() {
        if (!this.decisionForm.valid)
            return false;

        const selectedSectionId = this.decisionForm.get(AccountSettings.UWDecisionSection)?.value;

        if (Utils.isNullOrWhitespace(selectedSectionId))
            return false;

        return true;
    }

    isSectionWorkflowSelected() {
        const selectedSectionId = this.decisionForm?.get(AccountSettings.UWDecisionSection)?.value;
        return !!selectedSectionId && selectedSectionId !== this.noSection.id;
    }

    getAccountSetting(settingName: AccountSettings = AccountSettings.UWDecisionSection) {
        const uwDecisionSectionSetting = this.accountSettings?.filter(x => x.name === settingName);

        if (!uwDecisionSectionSetting?.length) {
            return null;
        }

        if (uwDecisionSectionSetting?.length > 1) {
            console.error(`Multiple account settings found. [${settingName}]`);
            return null;
        }

        const accountSetting = uwDecisionSectionSetting[0];
        return accountSetting;
    }

    getAccountSettingDate(settingName: AccountSettings) {
        const accountSetting = this.getAccountSetting(settingName);
        const modifiedOrCreatedDate = accountSetting?.lastModifiedDate ?? accountSetting?.creationDate;

        if (!modifiedOrCreatedDate) return null;
        return TimeUtils.convertToDateCst(modifiedOrCreatedDate);
    }

    updateLastUpdated() {
        const sectionSettingDate = this.getAccountSettingDate(AccountSettings.UWDecisionSection);
        const ruleSettingDate = this.getAccountSettingDate(AccountSettings.UWDecisionDisableUpdateRuleId);

        if (!sectionSettingDate && !ruleSettingDate) {
            this.lastUpdated = null;
            return;
        }

        if (!sectionSettingDate) {
            this.lastUpdated = ruleSettingDate;
            return;
        }

        if (!ruleSettingDate) {
            this.lastUpdated = sectionSettingDate;
            return;
        }

        this.lastUpdated = sectionSettingDate > ruleSettingDate ? sectionSettingDate : ruleSettingDate;
    }

    setDecisionForm() {
        const uwDecisionSectionSetting = this.getAccountSetting(AccountSettings.UWDecisionSection);
        const initialSection = this.sections.find(x => x.id === uwDecisionSectionSetting?.value);

        this.decisionForm.get(AccountSettings.UWDecisionSection).setValue(initialSection?.id ?? this.noSection);
        this.initialSectionId = initialSection?.id ?? this.noSection.id;
        this.updateLastUpdated();
    }

    setDecisionRuleForm() {
        const uwDecisionRuleSetting = this.getAccountSetting(AccountSettings.UWDecisionDisableUpdateRuleId);
        const initialRule = this.rules.find(x => x.id === uwDecisionRuleSetting?.value);

        this.decisionForm.get(AccountSettings.UWDecisionDisableUpdateRuleId).setValue(initialRule?.id ?? this.noRule.id);
        this.initialRuleId = initialRule?.id ?? this.noRule.id;
        this.updateLastUpdated();
    }

    //#endregion
    //#region Subscriptions

    subscribeToSections() {
        this.subs.add(this.sectionDataService.loadSectionsForWorkflow(this.account.workflowId).subscribe(sections => {
            this.sections = [this.noSection, ...sections];
            this.setDecisionForm();
            this.sectionsLoaded = true;
        }));
    }

    subscribeToRules() {
        this.subs.add(this.rulesDataService.load(this.account.id).subscribe(rules => {
            this.rules = [this.noRule, ...rules];
            this.setDecisionRuleForm();
            this.rulesLoaded = true;
        }));
    }

    subscribeToAccountSettings() {
        this.subs.add(this.accountSettingsDataService.loadAccountSettings(this.account.id).subscribe(accountSettings => {
            this.accountSettings = accountSettings;
            this.setDecisionForm();
            this.setDecisionRuleForm();
            this.accountSettingsLoaded = true;
        }));
    }

    //#endregion
    //#region Lifecycle

    ngOnInit() {
        this.decisionForm = this.formBuilder.group({
            [AccountSettings.UWDecisionSection]: null,
            [AccountSettings.UWDecisionDisableUpdateRuleId]: null,
        });
        this.subscribeToSections();
        this.subscribeToRules();
        this.subscribeToAccountSettings();
    }

    ngOnDestroy() {
        this.subs.unsubscribe();
    }

    //#endregion
    //#region Handlers

    handleSectionChange(change: MatSelectChange) {
        if (this.initialSectionId === change.value) return;
        this.save().subscribe(); // "Auto-Save"

        if (!this.isSectionWorkflowSelected()) {
            this.decisionForm.get(AccountSettings.UWDecisionDisableUpdateRuleId).setValue(this.noRule.id);
            this.save(AccountSettings.UWDecisionDisableUpdateRuleId).subscribe(); // Keep disable rule unset when no section workflow is selected
        }
    }

    handleRuleChange(selectedRuleId: string) {
        if (this.initialRuleId === selectedRuleId) return;
        this.save(AccountSettings.UWDecisionDisableUpdateRuleId).subscribe(); // "Auto-Save"
    }

    save(settingName: AccountSettings = AccountSettings.UWDecisionSection) {
        if (settingName === AccountSettings.UWDecisionSection && !this.isValid())
            return of(false);

        const existingAccountSetting = this.accountSettings.find(x => x.name === settingName);
        const selectedValue = this.decisionForm.get(settingName)?.value;

        const updateAccountSetting: AccountSettingsDTO = {
            'id': existingAccountSetting?.id ?? Utils.emptyGuid,
            'accountId': this.account.id,
            'name': settingName,
            'value': selectedValue,
        };

        const saveSettings$ = this.appService.postData<UWResponse<AccountSettingsDTO[]>>('AccountSettings', [updateAccountSetting]).pipe(
            map(_response => {
                // We don't use the response. We'll reload our observable so all subscribers will update.
                this.accountSettingsDataService.reloadAccountSettings(this.account.id).subscribe();
                if (settingName === AccountSettings.UWDecisionSection) {
                    this.initialSectionId = selectedValue;
                }
                if (settingName === AccountSettings.UWDecisionDisableUpdateRuleId) {
                    this.initialRuleId = selectedValue;
                }

                return true;
            }),
            catchError((err: Error) => {
                this.errorMessage = err.message;
                return of(false);
            }),
        );

        return saveSettings$;
    }

    //#endregion
}